import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

// Beacon-pointed Axios instance. The Beacon backend lives in a separate
// repo (`beacon-standalone/`, on `:4000`) so all `/beacon/*`,
// `/aeo-query/*`, `/past-audits/*`, `/monitor/*` calls route through here
// rather than through the default Antares-pointed instance in `./axios`.
//
// Auth: Antares stores the access token in an HttpOnly cookie that JS
// can't read. To call beacon-standalone (a different origin) we need a
// Bearer token, so we fetch the decrypted JWT from
// `GET /api/auth/beacon-token` (Antares — same origin, cookie auth) and
// cache it in memory for the page lifetime. On 401, the cache is busted
// and the call retries once with a freshly fetched token, which absorbs
// token expiry transparently.

const handleAuthFailure = () => {
  cachedToken = null;
  if (window.location.pathname === "/login") return;
  window.location.href = "/login";
};

const AntaresAxios = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: true,
});

let cachedToken: string | null = null;
let inFlightFetch: Promise<string | null> | null = null;

const fetchToken = async (): Promise<string | null> => {
  if (cachedToken) return cachedToken;
  if (inFlightFetch) return inFlightFetch;
  inFlightFetch = (async () => {
    try {
      const res = await AntaresAxios.get("/auth/beacon-token");
      const token: string | null = res?.data?.data?.accessToken ?? null;
      cachedToken = token;
      return token;
    } catch {
      return null;
    } finally {
      inFlightFetch = null;
    }
  })();
  return inFlightFetch;
};

const BeaconAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BEACON_API_URL}/api`,
  timeout: 200000,
});

BeaconAxiosInstance.interceptors.request.use(
  async (config) => {
    const token = await fetchToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

BeaconAxiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    if (
      error.response?.status === 401 &&
      originalConfig &&
      !originalConfig._retried
    ) {
      // Cookie may still be valid even though the cached JWT is stale —
      // bust the cache, refetch from Antares, retry the original call once.
      cachedToken = null;
      const fresh = await fetchToken();
      if (fresh) {
        originalConfig._retried = true;
        originalConfig.headers.Authorization = `Bearer ${fresh}`;
        return BeaconAxiosInstance.request(originalConfig);
      }
      // Antares also rejected — the session is genuinely gone.
      if (!import.meta.env.DEV) handleAuthFailure();
    }

    return Promise.reject(error);
  },
);

export default BeaconAxiosInstance;
